export type HazardStatus =
  | 'DRAFT'
  | 'IDENTIFIED'
  | 'UNDER_REVIEW'
  | 'ACTIVE'
  | 'MITIGATED'
  | 'ACCEPTED'
  | 'RETIRED'
  | 'CLOSED';

export type HazardSeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type HazardLikelihood = 'RARE' | 'UNLIKELY' | 'POSSIBLE' | 'PROBABLE' | 'FREQUENT';

export type CauseType =
  | 'HUMAN'
  | 'TECHNICAL'
  | 'ORGANIZATIONAL'
  | 'ENVIRONMENTAL'
  | 'PROCEDURAL';

export type ConsequenceSeverity =
  | 'NEGLIGIBLE'
  | 'MINOR'
  | 'MAJOR'
  | 'HAZARDOUS'
  | 'CATASTROPHIC';

export type ControlType = 'PREVENTIVE' | 'MITIGATIVE' | 'RECOVERY' | 'DETECTIVE';
export type ControlEffectiveness = 'SATISFACTORY' | 'PARTIAL' | 'INEFFECTIVE' | 'UNDER_EVALUATION';

export type SourceType =
  | 'REPORT'
  | 'OCCURRENCE'
  | 'INVESTIGATION'
  | 'AUDIT'
  | 'MOC'
  | 'SURVEY'
  | 'FDA';

export type ReviewVerdict =
  | 'MAINTAIN_CONTROLS'
  | 'REASSESS_RISK'
  | 'ACTION_REQUIRED'
  | 'RETIRE';

export interface HazardCategory {
  id: string;
  organizationId: string;
  code: string;
  name: string;
  parentId?: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const FALLBACK_HAZARD_CATEGORIES: HazardCategory[] = [
  { id: 'CAT-FLTMGMT', organizationId: 'ORG-XYZ', code: 'FLT_MGMT', name: 'FLIGHT MANAGEMENT / FLIGHT PATH MANAGEMENT', description: 'Flight path management, automated flight systems, energy state, trajectory tracking, and FMS navigation.', isActive: true, createdAt: '', updatedAt: '' },
  { id: 'CAT-FLTOPS', organizationId: 'ORG-XYZ', code: 'FLT_OPS', name: 'Flight Operations & Cockpit', description: 'Hazards during flight phases, takeoff, cruise, approach and landing', isActive: true, createdAt: '', updatedAt: '' },
  { id: 'CAT-GROUND', organizationId: 'ORG-XYZ', code: 'GROUND_OPS', name: 'Ground & Ramp Handling', description: 'Apron maneuvering, servicing, fueling, baggage and pushback', isActive: true, createdAt: '', updatedAt: '' },
  { id: 'CAT-MAINT', organizationId: 'ORG-XYZ', code: 'MAINTENANCE', name: 'Aircraft Maintenance & Engineering', description: 'Line maintenance, hangar checks, tool control and component reliability', isActive: true, createdAt: '', updatedAt: '' },
  { id: 'CAT-METEO', organizationId: 'ORG-XYZ', code: 'METEOROLOGY', name: 'Weather & Environmental Conditions', description: 'Icing, convective storms, microburst, turbulence and volcanic ash', isActive: true, createdAt: '', updatedAt: '' },
  { id: 'CAT-AIRSPACE', organizationId: 'ORG-XYZ', code: 'AIRSPACE_ATC', name: 'Airspace & Aerodrome Infrastructure', description: 'Runway surface, lighting, ATC workload, navigation aid outages', isActive: true, createdAt: '', updatedAt: '' },
  { id: 'CAT-CABIN', organizationId: 'ORG-XYZ', code: 'CABIN_PAX', name: 'Cabin & Passenger Operations', description: 'Unruly passenger, medical emergency, galley equipment, evacuation hazards', isActive: true, createdAt: '', updatedAt: '' },
  { id: 'CAT-SECURITY', organizationId: 'ORG-XYZ', code: 'SECURITY_DG', name: 'Security & Dangerous Goods', description: 'Lithium battery thermal runaway, undeclared cargo, cyber intrusion', isActive: true, createdAt: '', updatedAt: '' },
  { id: 'CAT-HUMAN', organizationId: 'ORG-XYZ', code: 'HUMAN_FACTORS', name: 'Human Factors & Fatigue', description: 'Duty time exceedance, circadian disruption, spatial disorientation', isActive: true, createdAt: '', updatedAt: '' },
];

export interface Hazard {
  id: string;
  organizationId: string;
  hazardNumber: string;
  title: string;
  conditionText: string;
  consequenceText: string;
  potentialOutcome?: string;
  categoryId: string;
  categoryName?: string;
  categoryCode?: string;
  status: HazardStatus;
  severityEstimate: HazardSeverity;
  likelihoodEstimate: HazardLikelihood;
  initialRiskIndex?: string;
  ownerId?: string;
  ownerName?: string;
  department?: string;
  location?: string;
  reviewFrequencyDays: number;
  nextReviewDate?: string;
  lastReviewedAt?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  causes?: HazardCause[];
  consequences?: HazardConsequence[];
  controls?: HazardControl[];
  sources?: HazardSource[];
  reviews?: HazardReview[];
  statusHistory?: HazardStatusHistory[];
  comments?: HazardComment[];
  attachments?: HazardAttachment[];
  linkedRisks?: {
    id: string;
    riskNumber: string;
    title: string;
    status: string;
    initialRiskIndex?: string;
  }[];
}

export interface HazardCause {
  id: string;
  hazardId: string;
  causeText: string;
  causeType: CauseType;
  createdAt: string;
}

export interface HazardConsequence {
  id: string;
  hazardId: string;
  consequenceText: string;
  severityLevel: ConsequenceSeverity;
  createdAt: string;
}

export interface HazardControl {
  id: string;
  hazardId: string;
  controlTitle: string;
  controlType: ControlType;
  description: string;
  effectiveness: ControlEffectiveness;
  responsibleDepartment?: string;
  isActive: boolean;
  createdAt: string;
}

export interface HazardSource {
  id: string;
  hazardId: string;
  sourceType: SourceType;
  sourceReferenceId: string;
  description?: string;
  createdAt: string;
}

export interface HazardReview {
  id: string;
  hazardId: string;
  reviewerId: string;
  reviewerName: string;
  reviewDate: string;
  verdict: ReviewVerdict;
  comments: string;
  nextReviewDate: string;
  createdAt: string;
}

export interface HazardStatusHistory {
  id: string;
  hazardId: string;
  fromStatus: HazardStatus;
  toStatus: HazardStatus;
  reason?: string;
  changedBy: string;
  changedAt: string;
}

export interface HazardComment {
  id: string;
  hazardId: string;
  userId: string;
  userName: string;
  commentText: string;
  createdAt: string;
}

export interface HazardAttachment {
  id: string;
  hazardId: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  storagePath: string;
  uploadedBy: string;
  uploadedAt: string;
}

export interface HazardDashboardStats {
  totalHazards: number;
  activeHazards: number;
  underReviewHazards: number;
  mitigatedHazards: number;
  closedOrRetiredHazards: number;
  overdueReviewCount: number;
  categoryBreakdown: Record<string, number>;
  statusBreakdown: Record<string, number>;
  severityBreakdown: Record<string, number>;
}
